import React, { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption, SyutGrid, type SyutGridColumn } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { reportService, partyService } from 'api/services';
import type {
  AgeingBucket,
  DebtorsAgeingSummaryRow,
  DebtorsAgeingDetailRow,
  DebtorsAgeingCurrencySubtotal,
} from 'api/types';
import { useHasPermission } from 'hooks/usePermissions';
import { useAuthContext } from 'contexts/AuthContext';
import ReportHeader from 'components/reports/ReportHeader';
import DrillableCell from 'components/reports/DrillableCell';

const today = () => new Date().toISOString().slice(0, 10);
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');

const inp: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };

// Default bucket set — the audit-firm 90-day pattern. Users can override via
// the "Bucket edges" filter (parses comma / space separated integers into
// {from,to,label} triples). Kept in sync with seed 111 for the server-side
// fallback when a caller omits the buckets payload.
const DEFAULT_BUCKET_EDGES = '90, 180, 270, 365';

const DEFAULT_BUCKETS: AgeingBucket[] = [
  { label: '0-90',    from: 0,   to: 90 },
  { label: '91-180',  from: 91,  to: 180 },
  { label: '181-270', from: 181, to: 270 },
  { label: '271-365', from: 271, to: 365 },
  { label: '>365',    from: 366, to: null },
];

// Turn "90, 180, 270, 365" (or space / hyphen separated) into a bucket set.
// Only positive integer edges are kept; duplicates dropped; auto-sorted. The
// final bucket is always the open-ended ">last-edge" tail. Falls back to the
// default set if nothing parses (so the report still runs on an empty field).
const parseBucketEdges = (text: string): AgeingBucket[] => {
  const edges = [...new Set(
    text
      .split(/[,\s-]+/)
      .map(s => Number(s.trim()))
      .filter(n => Number.isInteger(n) && n > 0),
  )].sort((a, b) => a - b);

  if (edges.length === 0) return DEFAULT_BUCKETS;

  const out: AgeingBucket[] = [];
  for (let i = 0; i < edges.length; i++) {
    const from = i === 0 ? 0 : edges[i - 1] + 1;
    const to = edges[i];
    out.push({ label: `${from}-${to}`, from, to });
  }
  const last = edges[edges.length - 1];
  out.push({ label: `>${last}`, from: last + 1, to: null });
  return out;
};

type ViewMode = 'SUMMARY' | 'DETAIL';
type CurrencyView = 'CONSOLIDATED' | 'BY_CURRENCY';
type AgeingBasis = 'INVOICE' | 'DUE';

interface DebtorsAgeingScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

// Discriminated union so SyutGrid can render both view-mode row types under one
// column set. mode='summary' → BucketAmounts + Total. mode='detail' → per-item.
type Row =
  | ({ mode: 'summary'; id: string } & DebtorsAgeingSummaryRow)
  | ({ mode: 'detail';  id: string } & DebtorsAgeingDetailRow);

const DebtorsAgeingScreen: React.FC<DebtorsAgeingScreenProps> = ({ moduleIcon, tabId }) => {
  const canExport = useHasPermission('debtors-ageing', 'EXPORT');

  // --- Filter state ---
  const [filterOpen, setFilterOpen] = useState(false);
  const [asOfDate, setAsOfDate] = useState(today());
  const [ageingBasis, setAgeingBasis] = useState<AgeingBasis>('DUE');
  const [viewMode, setViewMode] = useState<ViewMode>('SUMMARY');
  const [currencyView, setCurrencyView] = useState<CurrencyView>('CONSOLIDATED');
  const [partyId, setPartyId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [minBalanceText, setMinBalanceText] = useState('');
  const [includeZero, setIncludeZero] = useState(false);
  const [bucketEdgesText, setBucketEdgesText] = useState<string>(DEFAULT_BUCKET_EDGES);
  const buckets = useMemo(() => parseBucketEdges(bucketEdgesText), [bucketEdgesText]);

  // --- Scope selection (multi-entity / multi-BU consolidation) ---
  // Options come from the user's accessible scope (/me/scope), so the pickers only
  // offer units the user is allowed to see; the backend re-validates on every call.
  const { accessibleEntities, accessibleBusinessUnits } = useAuthContext();
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
  const [selectedBuIds, setSelectedBuIds] = useState<string[]>([]);
  const entityOptions = useMemo<SelectOption[]>(
    () => accessibleEntities.map(e => ({ value: e.id, label: `${e.entityCode} — ${e.entityName}` })),
    [accessibleEntities],
  );
  // BUs narrow to the chosen entities (all accessible BUs when no entity is selected).
  const buOptions = useMemo<SelectOption[]>(() => {
    const scoped = selectedEntityIds.length > 0
      ? accessibleBusinessUnits.filter(b => selectedEntityIds.includes(b.entityId))
      : accessibleBusinessUnits;
    return scoped.map(b => ({ value: b.id, label: `${b.buCode} — ${b.buName}` }));
  }, [accessibleBusinessUnits, selectedEntityIds]);
  // Drop any selected BU that no longer belongs to the selected entities.
  useEffect(() => {
    if (selectedEntityIds.length === 0 || selectedBuIds.length === 0) return;
    const valid = new Set(buOptions.map(o => o.value));
    setSelectedBuIds(prev => prev.filter(id => valid.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityIds]);
  const partyLabelCache = useRef<Map<string, string>>(new Map());
  const partyLabel = partyId ? (partyLabelCache.current.get(partyId) || '') : '';

  // Mirror the SyutGrid's actual rendered column widths in the rollup band
  // below. A shadow CSS grid can't know the widths SyutGrid computes at runtime
  // (Party column has no explicit size and grows to fit long names), so we
  // measure the live <th> widths and mirror them. Re-measures on data / mode /
  // bucket changes and on any container resize.
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [colWidths, setColWidths] = useState<number[]>([]);


  // --- Result state — infinite scroll accumulator ---
  // Each Apply resets to page 1; scrolling near the bottom appends the next
  // page's rows. bucketTotals / grandTotal / totalCount / buckets are refreshed
  // from the latest response (they cover the FULL matching set on the server,
  // so successive pages return the same values).
  type AccResult<T> = {
    items: T[];
    buckets: AgeingBucket[];
    bucketTotals: number[];
    grandTotal: number;
    creditTotal: number;
    shortPaidTotal: number;
    netTotal: number;
    baseCurrencyCode: string;
    currencySubtotals: DebtorsAgeingCurrencySubtotal[];
    totalCount: number;
    lastPage: number;
  };
  const [summaryAcc, setSummaryAcc] = useState<AccResult<DebtorsAgeingSummaryRow> | null>(null);
  const [detailAcc,  setDetailAcc]  = useState<AccResult<DebtorsAgeingDetailRow>  | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting,   setExporting]   = useState(false);
  const [ran,         setRan]         = useState(false);
  const pageSize = 100;

  const activeBuckets = summaryAcc?.buckets ?? detailAcc?.buckets ?? buckets;
  const bucketTotals  = (summaryAcc?.bucketTotals ?? detailAcc?.bucketTotals ?? []) as number[];
  const grandTotal    = summaryAcc?.grandTotal ?? detailAcc?.grandTotal ?? 0;
  const creditTotal   = summaryAcc?.creditTotal ?? detailAcc?.creditTotal ?? 0;
  const shortPaidTotal = summaryAcc?.shortPaidTotal ?? detailAcc?.shortPaidTotal ?? 0;
  const netTotal     = summaryAcc?.netTotal ?? detailAcc?.netTotal ?? 0;
  // Footer totals are ALWAYS in base currency (ADR-0031). This is the base currency label.
  const baseCcy       = summaryAcc?.baseCurrencyCode ?? detailAcc?.baseCurrencyCode ?? '';
  // Per-currency subtotal bands (BY_CURRENCY summary only).
  const currencySubtotals = summaryAcc?.currencySubtotals ?? [];

  // --- Staleness tracking ---
  // Snapshot of the filter values at the moment the currently-visible report
  // was fetched. When the user changes ANY filter after Apply, the current
  // snapshot diverges from the applied one → the report is "stale" and we
  // warn the user (dim + ribbon) without wiping the data. Apply resets the
  // snapshot so the warning clears once the fetch succeeds.
  const currentSnapshot = useMemo(
    () => JSON.stringify({
      asOfDate, ageingBasis, viewMode, currencyView, partyId, currencyCode,
      minBalanceText, includeZero, bucketEdgesText, selectedEntityIds, selectedBuIds,
    }),
    [asOfDate, ageingBasis, viewMode, currencyView, partyId, currencyCode, minBalanceText, includeZero, bucketEdgesText, selectedEntityIds, selectedBuIds],
  );
  const currentSnapshotRef = useRef(currentSnapshot);
  currentSnapshotRef.current = currentSnapshot;
  const [appliedSnapshot, setAppliedSnapshot] = useState<string>('');
  const isStale = ran && appliedSnapshot !== '' && currentSnapshot !== appliedSnapshot;

  // --- Party async loader (CUSTOMER only) ---
  const loadParties = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await partyService.getAll({
        pageSize: 50,
        searchTerm: input || undefined,
        partyType: 'CUSTOMER',
      });
      res.data.forEach(p => partyLabelCache.current.set(p.id, `${p.partyCode} - ${p.partyName}`));
      return res.data.map(p => ({ value: p.id, label: `${p.partyCode} - ${p.partyName}` }));
    } catch {
      return [];
    }
  }, []);

  // --- Data fetch ---
  // fetchPage(targetPage, append): fetches one page from the server. When
  // append=false the accumulator is REPLACED (fresh Apply, page 1). When
  // append=true the page's rows are APPENDED (infinite scroll).
  const fetchPage = useCallback(async (targetPage: number, append: boolean) => {
    const minBalance = minBalanceText ? Number(minBalanceText) : undefined;
    if (minBalanceText && Number.isNaN(minBalance)) {
      notificationService.toast.warning('Min balance must be a number.');
      return;
    }
    const params = {
      asOfDate,
      ageingBasis,
      viewMode,
      currencyView,
      partyId: partyId || undefined,
      currencyCode: currencyCode || undefined,
      minBalance,
      includeZeroBalance: includeZero,
      entityIds: selectedEntityIds.length > 0 ? selectedEntityIds : undefined,
      businessUnitIds: selectedBuIds.length > 0 ? selectedBuIds : undefined,
      page: targetPage,
      pageSize,
    };
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      if (viewMode === 'SUMMARY') {
        const r = await reportService.getDebtorsAgeingSummary(params, buckets);
        setSummaryAcc(prev => {
          if (!append || !prev) {
            return {
              items: r.items, buckets: r.buckets,
              bucketTotals: r.bucketTotals, grandTotal: r.grandTotal, creditTotal: r.creditTotal,
              shortPaidTotal: r.shortPaidTotal ?? 0, netTotal: r.netTotal,
              baseCurrencyCode: r.baseCurrencyCode, currencySubtotals: r.currencySubtotals,
              totalCount: r.totalCount, lastPage: targetPage,
            };
          }
          return {
            ...prev,
            items: [...prev.items, ...r.items],
            bucketTotals: r.bucketTotals, grandTotal: r.grandTotal, creditTotal: r.creditTotal,
            shortPaidTotal: r.shortPaidTotal ?? 0, netTotal: r.netTotal,
            baseCurrencyCode: r.baseCurrencyCode, currencySubtotals: r.currencySubtotals,
            totalCount: r.totalCount, lastPage: targetPage,
          };
        });
        setDetailAcc(null);
      } else {
        const r = await reportService.getDebtorsAgeingDetail(params, buckets);
        setDetailAcc(prev => {
          if (!append || !prev) {
            return {
              items: r.items, buckets: r.buckets,
              bucketTotals: r.bucketTotals, grandTotal: r.grandTotal, creditTotal: r.creditTotal,
              shortPaidTotal: r.shortPaidTotal ?? 0, netTotal: r.netTotal,
              baseCurrencyCode: r.baseCurrencyCode, currencySubtotals: [],
              totalCount: r.totalCount, lastPage: targetPage,
            };
          }
          return {
            ...prev,
            items: [...prev.items, ...r.items],
            bucketTotals: r.bucketTotals, grandTotal: r.grandTotal, creditTotal: r.creditTotal,
            shortPaidTotal: r.shortPaidTotal ?? 0, netTotal: r.netTotal,
            baseCurrencyCode: r.baseCurrencyCode, currencySubtotals: [],
            totalCount: r.totalCount, lastPage: targetPage,
          };
        });
        setSummaryAcc(null);
      }
      setRan(true);
      // Capture the filter snapshot at fetch-success time so the stale-ribbon
      // only lights up when the user changes filters AFTER Apply. Only on
      // fresh Applies — infinite-scroll page-2+ fetches reuse the snapshot.
      if (!append) setAppliedSnapshot(currentSnapshotRef.current);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load Debtors Ageing.');
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [asOfDate, ageingBasis, viewMode, currencyView, partyId, currencyCode, minBalanceText, includeZero, buckets, selectedEntityIds, selectedBuIds]);

  const run = useCallback(() => fetchPage(1, false), [fetchPage]);

  // Refs for the scroll-triggered loadMore so we can compute hasMore + next
  // page without adding acc state to the callback's dep list (which would
  // recreate the handler on every fetched page → detach/reattach the scroll
  // listener in the middle of a scroll).
  const summaryAccRef = useRef(summaryAcc);  summaryAccRef.current = summaryAcc;
  const detailAccRef  = useRef(detailAcc);   detailAccRef.current  = detailAcc;
  const viewModeRef   = useRef(viewMode);    viewModeRef.current   = viewMode;
  const loadingRef    = useRef(loading);     loadingRef.current    = loading;
  const loadingMoreRef = useRef(loadingMore); loadingMoreRef.current = loadingMore;

  const loadMore = useCallback(() => {
    if (loadingRef.current || loadingMoreRef.current) return;
    const acc = viewModeRef.current === 'SUMMARY' ? summaryAccRef.current : detailAccRef.current;
    if (!acc || acc.items.length >= acc.totalCount) return;
    fetchPage(acc.lastPage + 1, true);
  }, [fetchPage]);

  // Horizontal scroll offset of SyutGrid's internal .syut-grid-wrapper — used
  // to translate the totals band's inner content by the same amount so its
  // column cells stay under the header columns when the user pans sideways.
  const [scrollLeft, setScrollLeft] = useState(0);

  // Attach the infinite-scroll listener to SyutGrid's INTERNAL .syut-grid-wrapper
  // (not to our outer wrapper). Sticky <thead> sticks to the nearest scrolling
  // ancestor — that's .syut-grid-wrapper in the current layout — so scroll
  // events fire there, not on our outer wrapper. Same handler syncs the
  // horizontal offset for the totals band.
  useEffect(() => {
    if (!ran) return;
    const outer = gridWrapperRef.current;
    if (!outer) return;
    const scroller = outer.querySelector('.syut-grid-wrapper') as HTMLElement | null;
    if (!scroller) return;
    // Sync any initial scroll position (e.g. after remount) before wiring up.
    setScrollLeft(scroller.scrollLeft);
    const handleScroll = () => {
      // Horizontal sync — cheap; keep separate from vertical loadMore.
      setScrollLeft(scroller.scrollLeft);
      // Vertical infinite-scroll trigger.
      if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 200) {
        loadMore();
      }
    };
    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [loadMore, ran]);

  const clear = useCallback(() => {
    setAsOfDate(today());
    setAgeingBasis('DUE');
    setCurrencyView('CONSOLIDATED');
    setPartyId('');
    setCurrencyCode('');
    setMinBalanceText('');
    setIncludeZero(false);
    setBucketEdgesText(DEFAULT_BUCKET_EDGES);
    setSelectedEntityIds([]);
    setSelectedBuIds([]);
    setSummaryAcc(null);
    setDetailAcc(null);
    setRan(false);
    setAppliedSnapshot('');
  }, []);

  const handleExport = useCallback(async () => {
    if (!ran) { notificationService.toast.warning('Run the report before exporting.'); return; }
    const minBalance = minBalanceText ? Number(minBalanceText) : undefined;
    if (minBalanceText && Number.isNaN(minBalance)) {
      notificationService.toast.warning('Min balance must be a number.');
      return;
    }
    setExporting(true);
    try {
      await reportService.exportDebtorsAgeing({
        asOfDate,
        ageingBasis,
        viewMode,
        currencyView,
        partyId: partyId || undefined,
        currencyCode: currencyCode || undefined,
        minBalance,
        includeZeroBalance: includeZero,
        entityIds: selectedEntityIds.length > 0 ? selectedEntityIds : undefined,
        businessUnitIds: selectedBuIds.length > 0 ? selectedBuIds : undefined,
      }, buckets);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not export Debtors Ageing.');
    } finally {
      setExporting(false);
    }
  }, [ran, asOfDate, ageingBasis, viewMode, currencyView, partyId, currencyCode, minBalanceText, includeZero, buckets, selectedEntityIds, selectedBuIds]);

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(run),
    ...(canExport ? [{ ...actionButtonFactory.export_(handleExport), disabled: exporting }] : []),
  ];

  // ------------------------------------------------------------------
  // Grid rows + columns — differ by view mode. Summary has one column per
  // bucket (dynamic from activeBuckets); Detail has one column for the
  // bucket LABEL of each row.
  // ------------------------------------------------------------------

  const gridRows: Row[] = useMemo(() => {
    if (viewMode === 'SUMMARY' && summaryAcc) {
      return summaryAcc.items.map((r, i) => ({
        mode: 'summary' as const,
        id: `${r.partyId}-${r.currencyCode}-${i}`,
        ...r,
      }));
    }
    if (viewMode === 'DETAIL' && detailAcc) {
      return detailAcc.items.map(r => ({ mode: 'detail' as const, id: r.openItemId, ...r }));
    }
    return [];
  }, [viewMode, summaryAcc, detailAcc]);

  const totalCount = viewMode === 'SUMMARY'
    ? (summaryAcc?.totalCount ?? 0)
    : (detailAcc?.totalCount ?? 0);
  const loadedCount = gridRows.length;

  // Measures the SyutGrid header <th> widths so the rollup band below can
  // mirror them exactly. Re-runs on data / mode / bucket changes and on any
  // container resize (ResizeObserver on wrapper + inner table).
  useLayoutEffect(() => {
    const el = gridWrapperRef.current;
    if (!el) return;
    const measure = () => {
      const ths = el.querySelectorAll<HTMLTableCellElement>('thead tr th');
      if (ths.length === 0) return;
      setColWidths(Array.from(ths, th => th.getBoundingClientRect().width));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const table = el.querySelector('table');
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, [gridRows, viewMode, activeBuckets, loading]);

  const summaryColumns: SyutGridColumn<Row>[] = useMemo(() => [
    {
      id: 'party',
      header: 'Party',
      cell: ({ row }) => {
        const r = row.original;
        if (r.mode !== 'summary') return null;
        return (
          <DrillableCell>
            <strong>{r.partyName}</strong>
            <span style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
              {r.partyCode}
            </span>
            {r.entityCount > 1 && (
              <span title="Consolidated across multiple entities"
                style={{ marginLeft: '0.4rem', fontSize: '0.65rem', fontWeight: 600, color: 'var(--entity, #4f46e5)',
                  border: '1px solid currentColor', borderRadius: '4px', padding: '0 4px', whiteSpace: 'nowrap' }}>
                {r.entityCount} entities
              </span>
            )}
          </DrillableCell>
        );
      },
    },
    {
      id: 'currency',
      header: 'Ccy',
      size: 70,
      cell: ({ row }) => row.original.mode === 'summary' ? row.original.currencyCode : '',
    },
    ...activeBuckets.map((b, i) => ({
      id: `bucket-${i}`,
      header: b.label,
      align: 'right' as const,
      size: 110,
      cell: ({ row }: { row: { original: Row } }) => {
        const r = row.original;
        if (r.mode !== 'summary') return '';
        const amt = r.bucketAmounts[i] ?? 0;
        return amt === 0 ? '' : money(amt);
      },
    })),
    {
      id: 'total',
      header: 'Total',
      align: 'right',
      size: 130,
      cell: ({ row }) => row.original.mode === 'summary'
        ? <strong>{money(row.original.total)}</strong>
        : '',
    },
    {
      // ADR-0060: amounts the customer deducted and that are kept open — still owed, but
      // disputed, so they are not aged into a bucket.
      id: 'shortPaid',
      header: 'Short-paid',
      align: 'right',
      size: 120,
      cell: ({ row }) => {
        if (row.original.mode !== 'summary') return '';
        const s = row.original.shortPaid;
        return s ? <span title="Deducted by the customer, kept open (not aged)" style={{ color: 'var(--warning-text, #b45309)' }}>{money(s)}</span> : '';
      },
    },
    {
      id: 'credit',
      header: 'Credit',
      align: 'right',
      size: 120,
      cell: ({ row }) => {
        if (row.original.mode !== 'summary') return '';
        const c = row.original.unallocatedCredit;
        return c ? <span title="Unallocated receipts / advances" style={{ color: 'var(--bu, #0d9488)' }}>({money(c)})</span> : '';
      },
    },
    {
      id: 'net',
      header: 'Net',
      align: 'right',
      size: 130,
      cell: ({ row }) => {
        if (row.original.mode !== 'summary') return '';
        const r = row.original;
        // BY_CURRENCY: the amounts are in the transaction currency; show the base
        // equivalent as a muted sub-line so the row still ties to the base grand total.
        return (
          <div>
            <strong>{money(r.netOutstanding)}</strong>
            {currencyView === 'BY_CURRENCY' && (
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                ≈ {baseCcy} {money(r.baseNetOutstanding)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'items',
      header: 'Items',
      align: 'right',
      size: 70,
      cell: ({ row }) => row.original.mode === 'summary' ? row.original.openItemCount : '',
    },
  ], [activeBuckets, currencyView, baseCcy]);

  const detailColumns: SyutGridColumn<Row>[] = useMemo(() => [
    {
      id: 'party',
      header: 'Party',
      cell: ({ row }) => {
        const r = row.original;
        if (r.mode !== 'detail') return null;
        return (
          <DrillableCell>
            {r.partyName}
            <span style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
              {r.partyCode}
            </span>
          </DrillableCell>
        );
      },
    },
    {
      id: 'entity',
      header: 'Entity',
      size: 90,
      cell: ({ row }) => row.original.mode === 'detail' ? row.original.entityCode : '',
    },
    {
      id: 'bu',
      header: 'BU',
      size: 90,
      cell: ({ row }) => row.original.mode === 'detail' ? row.original.buCode : '',
    },
    {
      id: 'reference',
      header: 'Invoice',
      size: 130,
      cell: ({ row }) => {
        if (row.original.mode !== 'detail') return '';
        const r = row.original;
        // ADR-0060: a short-paid item says which bill it came from and why.
        const note = r.deductionKind
          ? [r.sourceReferenceNo ? `on ${r.sourceReferenceNo}` : null, r.reasonLabel, r.remarks].filter(Boolean).join(' · ')
          : '';
        return (
          <DrillableCell>
            {r.referenceNo}
            {note && (
              <div title={note} style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {note}
              </div>
            )}
          </DrillableCell>
        );
      },
    },
    {
      id: 'itemDate',
      header: 'Date',
      size: 100,
      cell: ({ row }) => row.original.mode === 'detail' ? fmtDate(row.original.itemDate) : '',
    },
    {
      id: 'dueDate',
      header: 'Due',
      size: 100,
      cell: ({ row }) => row.original.mode === 'detail' ? fmtDate(row.original.dueDate) : '',
    },
    {
      id: 'daysOverdue',
      header: 'Days',
      align: 'right',
      size: 70,
      cell: ({ row }) => row.original.mode === 'detail' ? row.original.daysOverdue : '',
    },
    {
      id: 'bucket',
      header: 'Bucket',
      size: 90,
      cell: ({ row }) => row.original.mode === 'detail' ? row.original.bucketLabel : '',
    },
    {
      id: 'currency',
      header: 'Ccy',
      size: 60,
      cell: ({ row }) => row.original.mode === 'detail' ? row.original.currencyCode : '',
    },
    {
      id: 'outstanding',
      header: 'Outstanding',
      align: 'right',
      size: 140,
      cell: ({ row }) => {
        if (row.original.mode !== 'detail') return '';
        const r = row.original;
        return (
          <div>
            {money(r.outstanding)}
            {currencyView === 'BY_CURRENCY' && (
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                ≈ {baseCcy} {money(r.baseOutstanding)}
              </div>
            )}
          </div>
        );
      },
    },
  ], [currencyView, baseCcy]);

  const columns = viewMode === 'SUMMARY' ? summaryColumns : detailColumns;

  return (
    <EnterpriseScreenShell
      title="Debtors Ageing"
      description="AR outstanding aged by bucket as of a reference date"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.clock size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        // Count = every field surfaced as a pill in the ReportHeader. The three
        // always-present operating parameters (As-of date, Ageing basis, View)
        // always add 1 each; the optional fields add 1 when non-empty. Gated
        // on `ran` so the badge hides on fresh login / screen load — before
        // Apply there is no report and hence no "applied" filters. Once the
        // user Applies, the badge shows a minimum of 3 (the operating params).
        appliedCount: ran
          ? (asOfDate ? 1 : 0)
            + (ageingBasis ? 1 : 0)
            + (viewMode ? 1 : 0)
            + (partyId ? 1 : 0)
            + (currencyCode ? 1 : 0)
            + (minBalanceText ? 1 : 0)
            + (includeZero ? 1 : 0)
            + (bucketEdgesText.trim() !== DEFAULT_BUCKET_EDGES ? 1 : 0)
          : 0,
      }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              {entityOptions.length > 1 && (
                <div className="filter-section">
                  <SyutSelect label="Entities" isMulti options={entityOptions}
                    value={selectedEntityIds}
                    onChange={v => setSelectedEntityIds((v as string[]) || [])}
                    placeholder="All my entities" size="sm" menuPortalTarget={document.body} />
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Consolidates outstanding across the selected entities. Empty = all your entities.
                  </div>
                </div>
              )}
              <div className="filter-section">
                <SyutSelect label="Business Units" isMulti options={buOptions}
                  value={selectedBuIds}
                  onChange={v => setSelectedBuIds((v as string[]) || [])}
                  placeholder="All business units" size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>As of Date</label>
                <input type="date" className="form-input" value={asOfDate}
                  onChange={e => setAsOfDate(e.target.value)} style={inp} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Ageing Basis" options={[
                  { value: 'DUE',     label: 'Due Date' },
                  { value: 'INVOICE', label: 'Invoice Date' },
                ]} value={ageingBasis}
                  onChange={v => setAgeingBasis((v as AgeingBasis) || 'DUE')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="View" options={[
                  { value: 'SUMMARY', label: 'Summary (per party)' },
                  { value: 'DETAIL',  label: 'Detail (per invoice)' },
                ]} value={viewMode}
                  onChange={v => setViewMode((v as ViewMode) || 'SUMMARY')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Currency" options={[
                  { value: 'CONSOLIDATED', label: 'Consolidated (base currency)' },
                  { value: 'BY_CURRENCY',  label: 'By transaction currency' },
                ]} value={currencyView}
                  onChange={v => setCurrencyView((v as CurrencyView) || 'CONSOLIDATED')}
                  isClearable={false} size="sm" />
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Consolidated converts every balance to base currency. By-currency groups each debtor per currency with a base-equivalent.
                </div>
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Bucket edges (days)</label>
                <input type="text" className="form-input" value={bucketEdgesText}
                  onChange={e => setBucketEdgesText(e.target.value)}
                  placeholder="e.g. 90, 180, 270, 365" style={inp} />
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Comma-separated. First bucket is 0-{buckets[0]?.to ?? '?'}; last is open-ended (&gt;{buckets[buckets.length - 2]?.to ?? '?'}).
                </div>
              </div>
              <div className="filter-section">
                <SyutSelect label="Party (optional)" isAsync loadOptions={loadParties}
                  defaultOptions cacheOptions={false}
                  seedOptions={partyId
                    ? [{ value: partyId, label: partyLabelCache.current.get(partyId) || '' }]
                    : undefined}
                  menuPortalTarget={document.body} value={partyId}
                  onChange={v => setPartyId((v as string) || '')}
                  placeholder="All customers" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Currency (optional)</label>
                <input type="text" className="form-input" value={currencyCode}
                  onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                  placeholder="e.g. INR" maxLength={3} style={inp} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Min Balance (summary)</label>
                <input type="number" className="form-input" value={minBalanceText}
                  onChange={e => setMinBalanceText(e.target.value)}
                  placeholder="0" style={inp} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeZero}
                    onChange={e => setIncludeZero(e.target.checked)} /> Include zero-balance
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clear}>Clear</button>
              <button className="btn btn-primary" onClick={run} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'As of', value: fmtDate(asOfDate) },
          { label: 'Basis', value: ageingBasis === 'DUE' ? 'Due Date' : 'Invoice Date' },
          { label: 'View',  value: viewMode === 'SUMMARY' ? 'Summary' : 'Detail' },
          { label: 'Currency', value: currencyView === 'CONSOLIDATED' ? `Consolidated${baseCcy ? ` (${baseCcy})` : ''}` : 'By currency' },
          ...(partyId    ? [{ label: 'Party',    value: partyLabel }]  : []),
          ...(currencyCode ? [{ label: 'Currency', value: currencyCode }] : []),
        ]}
      />
      {ran && viewMode === 'SUMMARY' && currencyView === 'BY_CURRENCY' && currencySubtotals.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.5rem', padding: '0.5rem 0.75rem',
          borderLeft: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)',
          background: 'var(--bg-secondary)', fontSize: '0.75rem',
        }}>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 600, alignSelf: 'center' }}>Per-currency net:</span>
          {currencySubtotals.map(cs => (
            <span key={cs.currencyCode} title={`${cs.partyCount} customer(s)`} style={{
              border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0.15rem 0.5rem',
              background: 'var(--bg-primary)', whiteSpace: 'nowrap',
            }}>
              <strong>{cs.currencyCode}</strong> {money(cs.netOutstanding)}
              <span style={{ color: 'var(--text-muted)', marginLeft: '0.35rem' }}>
                ≈ {baseCcy} {money(cs.baseNetOutstanding)}
              </span>
            </span>
          ))}
        </div>
      )}
      {!ran && (
        <div style={{
          color: 'var(--text-muted)', padding: '2rem', textAlign: 'center',
          border: '1px solid var(--border-color)', borderTop: 'none',
          borderRadius: '0 0 0.5rem 0.5rem',
        }}>
          Adjust filters and click Apply.
        </div>
      )}
      {ran && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
          border: '1px solid var(--border-color)', borderTop: 'none',
          borderRadius: '0 0 0.5rem 0.5rem', overflow: 'hidden',
        }}>
          {/* Stale-data ribbon — appears when the user has changed any filter
              value since the last successful Apply. The grid + totals below
              are dimmed via opacity so the user visually recognises the numbers
              are no longer in sync with the current filter state. Clicking
              Apply refreshes the report and clears the ribbon. */}
          {isStale && (
            <div style={{
              padding: '0.5rem 0.75rem',
              background: '#fef3c7',
              color: '#92400e',
              borderBottom: '1px solid #fde68a',
              fontSize: '0.8125rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexShrink: 0,
            }}>
              <IconSystem.info size={14} />
              <span>Filters changed — click <strong>Apply</strong> to refresh the report.</span>
            </div>
          )}
          {/* Flex-column wrapper with overflow: hidden so SyutGrid's internal
              .syut-grid-wrapper becomes the sole scroller. That's what makes
              the <thead>'s `position: sticky; top: 0` actually stick — sticky
              attaches to the nearest scrolling ancestor, and if MY wrapper
              scrolled while .syut-grid-wrapper didn't, the header would ride
              the body up. The onScroll for infinite-scroll therefore has to be
              attached to that internal wrapper (see useEffect below).
              ref also anchors the runtime <th> measurement for the rollup band. */}
          <div ref={gridWrapperRef}
            style={{
              flex: 1, minHeight: 0,
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              opacity: isStale ? 0.55 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            <SyutGrid<Row>
              data={gridRows}
              columns={columns}
              isLoading={loading}
              emptyMessage="No outstanding balances match the filters"
              enablePagination={false}
            />
            {/* Transient "Loading more…" indicator only — appears while a
                page is inbound and disappears the moment it lands. The
                previously-permanent "Showing X of Y" and "End of report"
                strips consumed dead space at the bottom of every report; the
                row count is now surfaced inside the totals band instead. */}
            {ran && loadingMore && (
              <div style={{
                padding: '0.5rem 0.75rem', textAlign: 'center',
                color: 'var(--text-secondary)', fontSize: '0.75rem',
                borderTop: '1px dashed var(--border-color)',
                flexShrink: 0,
              }}>
                Loading more… ({loadedCount} of {totalCount})
              </div>
            )}
          </div>
          {/* Per-bucket rollup band — server-side across the FULL matching set.
              Widths come from the measured `<th>` sizes (see useLayoutEffect
              above) so each total sits exactly under its column, regardless of
              how wide SyutGrid rendered the Party column for long names.
              SUMMARY column order (must match summaryColumns):
                [Party, Ccy, ...bucket N, Total, Items]
              DETAIL column order:
                [Party, Invoice, Date, Due, Days, Bucket, Ccy, Outstanding]
              For summary we map bucket totals + grand total under their
              respective columns; for detail we only anchor the Grand Total
              under Outstanding — the per-bucket totals still show as inline
              chips left of the Party cell since the DETAIL grid has no
              per-bucket columns. */}
          {gridRows.length > 0 && activeBuckets.length > 0 && colWidths.length > 0 && (() => {
            // Cell-padding for the footer must roughly match SyutGrid's <td>
            // padding so numbers visually center over the column numbers above.
            const cellPad: React.CSSProperties = { padding: '0.6rem 0.75rem', boxSizing: 'border-box' };
            // Outer band — stays viewport-wide; visible background + top-border
            // live here so they still fill the container even when the inner
            // row is translated horizontally to follow the grid's scroll.
            const outerBase: React.CSSProperties = {
              background: 'var(--bg-secondary)',
              borderTop: '1px solid var(--border-color)',
              overflow: 'hidden',
              flexShrink: 0,
              opacity: isStale ? 0.55 : 1,
              transition: 'opacity 0.2s ease',
            };
            // Inner row — sized to the FULL table width (sum of measured <th>
            // widths) and translated by `-scrollLeft` so its cells stay under
            // their column headers when the user pans sideways or the filter
            // panel reflows the columns.
            const innerBase: React.CSSProperties = {
              fontSize: '0.8125rem',
              fontWeight: 700,
              display: 'flex',
              width: colWidths.reduce((a, w) => a + w, 0),
              minWidth: colWidths.reduce((a, w) => a + w, 0),
              transform: `translateX(${-scrollLeft}px)`,
              willChange: 'transform',
            };
            const renderCell = (idx: number, content: React.ReactNode, align: 'left' | 'right' = 'right') => (
              <div key={idx} style={{
                width: colWidths[idx],
                minWidth: colWidths[idx],
                textAlign: align,
                color: content === '' ? undefined : 'var(--text-primary)',
                ...cellPad,
              }}>
                {content}
              </div>
            );

            if (viewMode === 'SUMMARY') {
              // Column layout: [Party, Ccy, bucket 0..N-1, Total, Short-paid, Credit, Net, Items]
              const n = activeBuckets.length;
              const cells: React.ReactNode[] = [];
              // 0: Party — label + subtle row-count so the "N of M" info the
              // old status strip carried is still surfaced without stealing a row.
              cells.push(renderCell(0, (
                <span style={{ color: 'var(--text-secondary)' }}>
                  Total{baseCcy ? ` · base ${baseCcy}` : ''}
                  {totalCount > 0 && (
                    <span style={{ color: 'var(--text-muted)', fontWeight: 500, marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                      · {loadedCount.toLocaleString()} of {totalCount.toLocaleString()} loaded
                    </span>
                  )}
                </span>
              ), 'left'));
              // 1: Ccy — blank
              cells.push(renderCell(1, ''));
              // 2..n+1: bucket totals
              for (let i = 0; i < n; i++) {
                cells.push(renderCell(2 + i, money(bucketTotals[i] ?? 0)));
              }
              // n+2: Grand Total (gross receivable)
              cells.push(renderCell(2 + n, money(grandTotal)));
              // n+3: Short-paid total (ADR-0060 — kept open, not aged)
              cells.push(renderCell(3 + n, shortPaidTotal ? money(shortPaidTotal) : ''));
              // n+4: Credit total (unallocated receipts/advances)
              cells.push(renderCell(4 + n, creditTotal ? `(${money(creditTotal)})` : ''));
              // n+5: Net total
              cells.push(renderCell(5 + n, money(netTotal)));
              // n+6: Items — blank
              cells.push(renderCell(6 + n, ''));
              return <div style={outerBase}><div style={innerBase}>{cells}</div></div>;
            }

            // DETAIL: [Party, Invoice, Date, Due, Days, Bucket, Ccy, Outstanding]
            // Grand Total anchored under Outstanding (last column). Per-bucket
            // totals rendered as inline chips inside the Party cell (wide).
            const lastIdx = colWidths.length - 1;
            return (
              <div style={outerBase}>
                <div style={innerBase}>
                  <div style={{
                    width: colWidths[0], minWidth: colWidths[0],
                    ...cellPad,
                    textAlign: 'left',
                    display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap',
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Total{baseCcy ? ` · base ${baseCcy}` : ''}:</span>
                    {activeBuckets.map((b, i) => (
                      <span key={i} style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                        <span style={{ marginRight: '0.25rem' }}>{b.label}</span>
                        <strong style={{ color: 'var(--text-primary)' }}>{money(bucketTotals[i] ?? 0)}</strong>
                      </span>
                    ))}
                    {shortPaidTotal !== 0 && (
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                        <span style={{ marginRight: '0.25rem' }}>Short-paid</span>
                        <strong style={{ color: 'var(--text-primary)' }}>{money(shortPaidTotal)}</strong>
                      </span>
                    )}
                    {creditTotal !== 0 && (
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                        <span style={{ marginRight: '0.25rem' }}>Credit</span>
                        <strong style={{ color: 'var(--text-primary)' }}>({money(creditTotal)})</strong>
                      </span>
                    )}
                    {(shortPaidTotal !== 0 || creditTotal !== 0) && (
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                        <span style={{ marginRight: '0.25rem' }}>Net</span>
                        <strong style={{ color: 'var(--text-primary)' }}>{money(netTotal)}</strong>
                      </span>
                    )}
                  </div>
                  {/* Middle columns — spacers so the Outstanding cell aligns */}
                  {colWidths.slice(1, lastIdx).map((w, i) => (
                    <div key={i + 1} style={{ width: w, minWidth: w, ...cellPad }} />
                  ))}
                  {/* Last column — Grand Total under Outstanding */}
                  {renderCell(lastIdx, money(grandTotal))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </EnterpriseScreenShell>
  );
};

export default DebtorsAgeingScreen;
